import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

/**
 * Formata CNPJ para exibição
 */
const formatarCnpj = (cnpj) => {
  if (!cnpj) return "";
  const cnpjLimpo = cnpj.replace(/\D/g, "");
  if (cnpjLimpo.length === 14) {
    return cnpjLimpo.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  }
  return cnpj;
};

/**
 * Formata valor monetário para exibição
 */
const formatarMoeda = (valor = 0) => {
  const num = typeof valor === "number" ? valor : parseFloat(valor) || 0;
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(num);
};

/**
 * Formata número para exibição brasileira
 */
const formatarNumero = (numero) => {
  if (numero === null || numero === undefined || numero === "") return "0,00";
  const num = typeof numero === "number" ? numero : parseFloat(numero) || 0;
  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

/**
 * Exporta dados da Base Legal (Tela 3) para PDF
 * @param {Object} params - Parâmetros de exportação
 * @param {Array} params.linhas - Array de linhas agrupadas por base legal
 * @param {Array} params.basesLegais - Array de bases legais para mapear IDs para descrições
 * @param {Object} params.infoCabecalho - Informações do cabeçalho
 * @param {Object} params.totais - Totais a serem exibidos
 */
export const exportarBaseLegalPDF = async ({
  linhas = [],
  basesLegais = [],
  infoCabecalho = {},
  totais = {},
}) => {
  try {
    if (!Array.isArray(linhas) || linhas.length === 0) {
      throw new Error("Nenhum dado disponível para exportar");
    }

    // Processar dados ANTES de criar o HTML para evitar problemas de escopo
    // Buscar descrição da base legal e baseLegalCredito para cada linha
    const linhasProcessadas = linhas.map((linha) => {
      const baseLegal = basesLegais.find((b) => b.id === linha.baseLegalId || b.id_base_legal === linha.baseLegalId);
      return {
        ...linha,
        descricaoBaseLegal: baseLegal?.descricao || `Base Legal #${linha.baseLegalId}`,
        baseLegalCredito: baseLegal?.baseLegalCredito || baseLegal?.base_legal_credito || null,
      };
    });

    // Criar HTML do PDF
    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: 'Arial', sans-serif;
              padding: 20px;
              background: #ffffff;
            }
            .card {
              background: #ffffff;
              border-radius: 14px;
              padding: 20px;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            }
            .infoGrid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 15px;
              padding: 15px;
              background: #f8fafc;
              border-radius: 12px;
              border: 1px solid #e1e6ee;
              margin-bottom: 20px;
            }
            .infoGridFull {
              grid-column: 1 / -1;
            }
            .infoItem {
              display: flex;
              flex-direction: column;
              gap: 5px;
            }
            .infoItem span {
              font-size: 12px;
              color: #64748b;
            }
            .infoItem strong {
              color: #0f172a;
              font-size: 14px;
              font-weight: 600;
            }
            .totalsRow {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 15px;
              margin-bottom: 20px;
            }
            .totalCard {
              border-radius: 12px;
              padding: 15px;
              color: #ffffff;
              display: flex;
              flex-direction: column;
              gap: 8px;
              align-items: center;
              text-align: center;
            }
            .totalCard span {
              font-size: 12px;
              color: rgba(255, 255, 255, 0.9);
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            .totalCard strong {
              font-size: 18px;
              color: #ffffff;
              font-weight: 600;
            }
            .totalEcd {
              background: #1e3a8a;
            }
            .totalPis {
              background: #2563eb;
            }
            .totalCofins {
              background: #dc2626;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
              border: 1px solid #dfe3eb;
              border-radius: 12px;
              overflow: hidden;
            }
            thead {
              background: #f5f7fb;
            }
            th {
              padding: 12px;
              text-align: left;
              font-weight: 600;
              font-size: 12px;
              text-transform: uppercase;
              color: #1f2a44;
              border-bottom: 1px solid #dfe3eb;
            }
            th.text-right {
              text-align: right;
            }
            td {
              padding: 10px 12px;
              font-size: 11px;
              color: #1f2a44;
              border-bottom: 1px solid #e1e6ee;
            }
            td.text-right {
              text-align: right;
            }
            tbody tr:nth-child(even) {
              background-color: #fbfcff;
            }
            tbody tr:hover {
              background-color: #f9fbff;
            }
            .baseLegalCell {
              display: flex;
              flex-direction: column;
              gap: 4px;
            }
            .baseLegalCredito {
              font-size: 10px;
              color: #64748b;
              font-style: italic;
            }
          </style>
        </head>
        <body>
          <div class="card">
            <!-- Cabeçalho de Informações -->
            <div class="infoGrid">
              <div class="infoItem">
                <span>Empresa</span>
                <strong>${infoCabecalho.empresaNome || "Não informado"}</strong>
              </div>
              <div class="infoItem">
                <span>CNPJ</span>
                <strong>${formatarCnpj(infoCabecalho.cnpjEmpresa) || "Não informado"}</strong>
              </div>
              <div class="infoItem">
                <span>Período</span>
                <strong>${infoCabecalho.dtInicial && infoCabecalho.dtFinal 
                  ? `${infoCabecalho.dtInicial} a ${infoCabecalho.dtFinal}`
                  : infoCabecalho.dtInicial || infoCabecalho.dtFinal || "Não informado"}</strong>
              </div>
              <div class="infoItem">
                <span>Temas selecionados</span>
                <strong>${infoCabecalho.temasSelecionados || "Não informado"}</strong>
              </div>
            </div>

            <!-- Cards de Totais -->
            <div class="totalsRow">
              <div class="totalCard totalEcd">
                <span>Total ECD</span>
                <strong>${formatarMoeda(totais.valorTotalEcd || 0)}</strong>
              </div>
              <div class="totalCard totalPis">
                <span>Total PIS (1,65%)</span>
                <strong>${formatarMoeda(totais.pis || 0)}</strong>
              </div>
              <div class="totalCard totalCofins">
                <span>Total COFINS (7,6%)</span>
                <strong>${formatarMoeda(totais.cofins || 0)}</strong>
              </div>
            </div>

            <!-- Tabela -->
            <table>
              <thead>
                <tr>
                  <th>Conta Contábil</th>
                  <th class="text-right">Valor Total</th>
                  <th class="text-right">PIS</th>
                  <th class="text-right">COFINS</th>
                  <th>Base Legal de Crédito</th>
                </tr>
              </thead>
              <tbody>
                ${linhasProcessadas
                  .map((linha) => {
                    return `
                      <tr>
                        <td>${linha.descricaoBaseLegal || `Base Legal #${linha.baseLegalId}`}</td>
                        <td class="text-right">${formatarNumero(linha.valorTotalEcd)}</td>
                        <td class="text-right">${formatarNumero(linha.pis)}</td>
                        <td class="text-right">${formatarNumero(linha.cofins)}</td>
                        <td>
                          <div class="baseLegalCell">
                            ${linha.baseLegalCredito ? `<div class="baseLegalCredito">${linha.baseLegalCredito}</div>` : "-"}
                          </div>
                        </td>
                      </tr>
                    `;
                  })
                  .join("")}
                <!-- Linha de Totais -->
                <tr style="background-color: #f5f7fb; font-weight: 600;">
                  <td><strong>TOTAL</strong></td>
                  <td class="text-right"><strong>${formatarNumero(totais.valorTotalEcd || 0)}</strong></td>
                  <td class="text-right"><strong>${formatarNumero(totais.pis || 0)}</strong></td>
                  <td class="text-right"><strong>${formatarNumero(totais.cofins || 0)}</strong></td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        </body>
      </html>
    `;

    // Criar iframe completamente isolado (mesma abordagem do exportarResumoGeralPDF)
    // O iframe é renderizado em contexto separado, não afeta a página principal
    const iframe = document.createElement("iframe");
    iframe.style.cssText = `
      position: fixed;
      top: -10000px;
      left: -10000px;
      width: 210mm;
      height: 297mm;
      border: none;
      opacity: 0;
      pointer-events: none;
      z-index: -99999;
    `;
    
    document.body.appendChild(iframe);

    // Aguardar iframe carregar
    await new Promise((resolve) => {
      iframe.onload = resolve;
      iframe.src = "about:blank";
    });

    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(htmlContent);
    iframeDoc.close();

    // Aguardar renderização do conteúdo no iframe
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Verificar se há conteúdo no iframe
    const element = iframeDoc.querySelector(".card");
    if (!element) {
      console.error("❌ Container .card não encontrado no HTML");
      document.body.removeChild(iframe);
      throw new Error("Erro ao criar conteúdo do PDF: elemento .card não encontrado");
    }

    // Garantir que o body do iframe tenha estilos corretos
    iframeDoc.body.style.cssText = "margin: 0; padding: 0; width: 100%; background: #ffffff;";
    
    // Aguardar mais um pouco para garantir renderização completa
    await new Promise((resolve) => setTimeout(resolve, 800));

    // Gerar nome do arquivo
    const dataAtual = new Date().toISOString().split("T")[0].replace(/-/g, "");
    const cnpjLimpo = infoCabecalho.cnpjEmpresa
      ? infoCabecalho.cnpjEmpresa.replace(/\D/g, "")
      : "";
    const nomeArquivo =
      cnpjLimpo && cnpjLimpo.length === 14
        ? `BaseLegal_${cnpjLimpo}_${dataAtual}.pdf`
        : `BaseLegal_${dataAtual}.pdf`;

    try {
      // Verificar se o elemento tem conteúdo antes de capturar
      if (element.scrollHeight === 0 || element.scrollWidth === 0) {
        throw new Error("Elemento sem dimensões válidas");
      }

      // Capturar o elemento como canvas usando html2canvas
      // O elemento está dentro do iframe isolado, não afeta a página principal
      const canvas = await html2canvas(element, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        windowWidth: element.scrollWidth || 800,
        windowHeight: element.scrollHeight || 1200,
        foreignObjectRendering: false,
        allowTaint: false,
        backgroundColor: "#ffffff",
      });

      if (!canvas || canvas.width === 0 || canvas.height === 0) {
        throw new Error("Canvas vazio ou inválido");
      }

      // Converter canvas para imagem
      const imgData = canvas.toDataURL("image/jpeg", 0.95);
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      // Criar PDF usando jsPDF
      const pdf = new jsPDF({
        unit: "mm",
        format: "a4",
        orientation: "portrait",
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pageWidth - (margin * 2);
      const contentHeight = pageHeight - (margin * 2);

      // Calcular dimensões da imagem para caber na página
      const imgWidthMM = (imgWidth * 0.264583); // Converter px para mm (1px = 0.264583mm a 96dpi)
      const imgHeightMM = (imgHeight * 0.264583);
      
      // Calcular escala para caber na página mantendo proporção
      const scaleX = contentWidth / imgWidthMM;
      const scaleY = contentHeight / imgHeightMM;
      const scale = Math.min(scaleX, scaleY, 1); // Não aumentar, apenas reduzir se necessário

      const finalWidth = imgWidthMM * scale;
      const finalHeight = imgHeightMM * scale;

      // Centralizar na página
      const x = margin + (contentWidth - finalWidth) / 2;
      const y = margin;

      // Adicionar imagem ao PDF
      pdf.addImage(imgData, "JPEG", x, y, finalWidth, finalHeight);

      // Se a imagem for maior que uma página, adicionar páginas adicionais
      let heightLeft = finalHeight - contentHeight;
      let position = -contentHeight;

      while (heightLeft > 0) {
        pdf.addPage();
        position = position - contentHeight;
        pdf.addImage(
          imgData,
          "JPEG",
          x,
          position,
          finalWidth,
          finalHeight
        );
        heightLeft -= contentHeight;
      }

      // Salvar PDF
      pdf.save(nomeArquivo);
    } finally {
      // Remover iframe
      if (iframe && document.body.contains(iframe)) {
        document.body.removeChild(iframe);
      }
    }

    return {
      sucesso: true,
      nomeArquivo,
    };
  } catch (error) {
    console.error("Erro ao exportar Base Legal para PDF:", error);
    throw new Error(
      error.message || "Erro ao gerar arquivo PDF. Por favor, tente novamente."
    );
  }
};

